import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'jobseeker',
    location: '',
    experienceLevel: '',
    companyName: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const indianCities = [
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi' },
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'chennai', label: 'Chennai' },
    { value: 'kolkata', label: 'Kolkata' },
    { value: 'pune', label: 'Pune' },
    { value: 'ahmedabad', label: 'Ahmedabad' },
    { value: 'jaipur', label: 'Jaipur' },
    { value: 'surat', label: 'Surat' },
    { value: 'lucknow', label: 'Lucknow' },
    { value: 'kanpur', label: 'Kanpur' },
    { value: 'nagpur', label: 'Nagpur' },
    { value: 'indore', label: 'Indore' },
    { value: 'thane', label: 'Thane' },
    { value: 'bhopal', label: 'Bhopal' },
    { value: 'visakhapatnam', label: 'Visakhapatnam' },
    { value: 'patna', label: 'Patna' },
    { value: 'vadodara', label: 'Vadodara' },
    { value: 'ghaziabad', label: 'Ghaziabad' }
  ];

  const experienceLevels = [
    { value: 'fresher', label: 'Fresher (0-1 years)' },
    { value: 'entry', label: 'Entry Level (1-3 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (5-10 years)' },
    { value: 'lead', label: 'Lead/Manager (10+ years)' }
  ];

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score: 0, label: '', color: '' };

    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) score++;
    if (/\d/?.test(password)) score++;
    if (/[^a-zA-Z0-9]/?.test(password)) score++;

    const strengthMap = {
      0: { label: '', color: '' },
      1: { label: 'Weak', color: 'bg-error' },
      2: { label: 'Fair', color: 'bg-warning' },
      3: { label: 'Good', color: 'bg-accent' },
      4: { label: 'Strong', color: 'bg-success' },
      5: { label: 'Very Strong', color: 'bg-success' }
    };

    return { score, ...strengthMap?.[score] };
  };

  const validatePhone = (phone) => {
    if (!phone?.trim()) return true;
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex?.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (apiError) {
      setApiError('');
    }

    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData?.phone?.trim() && !validatePhone(formData?.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.location) {
      newErrors.location = 'Please select your location';
    }

    if (formData?.userType === 'jobseeker' && !formData?.experienceLevel) {
      newErrors.experienceLevel = 'Please select your experience level';
    }

    if (formData?.userType === 'employer' && !formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setApiError('');
    setSuccessMessage('');
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await signUp(formData);

      if (error) {
        setApiError(error?.message || 'Registration failed. Please try again.');
        setIsLoading(false);
        return;
      }

      setSuccessMessage('Account created successfully! Please check your email to confirm your account.');
      
      setTimeout(() => {
        navigate('/user-login');
      }, 2000);
    } catch (err) {
      setApiError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {apiError && (
        <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{apiError}</p>
        </div>
      )}

      {successMessage && (
        <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      <div className="space-y-4">
        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={(e) => handleInputChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          required
          disabled={isLoading}
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="your.email@example.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        <div>
          <Input
            label="Phone Number"
            type="tel"
            placeholder="9876543210"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            description="Optional - Enter 10-digit mobile number without +91"
            disabled={isLoading}
          />
        </div>

        <div>
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={formData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              error={errors?.password}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
          
          {formData?.password && passwordStrength?.label && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Password Strength:</span>
                <span className={`text-xs font-medium ${
                  passwordStrength?.score <= 2 ? 'text-error' : 
                  passwordStrength?.score === 3 ? 'text-warning' : 'text-success'
                }`}>
                  {passwordStrength?.label}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                  style={{ width: `${(passwordStrength?.score / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
            disabled={isLoading}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            I am a <span className="text-error">*</span>
          </label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleInputChange('userType', 'jobseeker')}
              className={`flex-1 p-4 rounded-lg border-2 transition-smooth ${
                formData?.userType === 'jobseeker' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              disabled={isLoading}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  formData?.userType === 'jobseeker' ?'border-primary' :'border-muted-foreground'
                }`}>
                  {formData?.userType === 'jobseeker' && (
                    <div className="w-3 h-3 rounded-full bg-primary" />
                  )}
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Job Seeker</div>
                  <div className="text-xs text-muted-foreground">Looking for opportunities</div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleInputChange('userType', 'employer')}
              className={`flex-1 p-4 rounded-lg border-2 transition-smooth ${
                formData?.userType === 'employer' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              disabled={isLoading}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  formData?.userType === 'employer' ?'border-primary' :'border-muted-foreground'
                }`}>
                  {formData?.userType === 'employer' && (
                    <div className="w-3 h-3 rounded-full bg-primary" />
                  )}
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Employer</div>
                  <div className="text-xs text-muted-foreground">Hiring candidates</div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <Select
          label="Location"
          placeholder="Select your city"
          options={indianCities}
          value={formData?.location}
          onChange={(value) => handleInputChange('location', value)}
          error={errors?.location}
          searchable
          required
          disabled={isLoading}
        />

        {formData?.userType === 'jobseeker' && (
          <Select
            label="Experience Level"
            placeholder="Select your experience level"
            options={experienceLevels}
            value={formData?.experienceLevel}
            onChange={(value) => handleInputChange('experienceLevel', value)}
            error={errors?.experienceLevel}
            required
            disabled={isLoading}
          />
        )}

        {formData?.userType === 'employer' && (
          <Input
            label="Company Name"
            type="text"
            placeholder="Enter your company name"
            value={formData?.companyName}
            onChange={(e) => handleInputChange('companyName', e?.target?.value)}
            error={errors?.companyName}
            required
            disabled={isLoading}
          />
        )}

        <div>
          <Checkbox
            label={
              <span className="text-sm text-foreground">
                I agree to the{' '}
                <a href="#" className="text-primary hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:underline">Privacy Policy</a>
              </span>
            }
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e?.target?.checked)}
            error={errors?.terms}
            disabled={isLoading}
          />
        </div>
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading}
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );
};

export default RegistrationForm;